// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import {
  Ticket,
  ticketCategorySchema,
  ticketSchema,
  ticketStatusSchema,
} from '@/ticket/api/types';
import { z } from 'zod';

export const getTicketsRequestSchema = z.object({
  status: ticketStatusSchema.optional(),
  category: ticketCategorySchema.array().optional(),
  search: z.string().optional(),
});

type GetTicketsRequest = z.infer<typeof getTicketsRequestSchema>;

const getTicketsResponseSchema = z.object({
  data: z.array(ticketSchema),
});

/**
 * Get tickets based on filter criteria
 */
export async function getTickets(
  request: GetTicketsRequest
): Promise<{ tickets: Ticket[] }> {
  const url = '/v1/ticket';
  const response = await api.get(url);
  const parsed = await parseResponse(getTicketsResponseSchema, response.data, {
    schemaName: 'Tickets',
    endpoint: url,
  });
  let filteredTickets = parsed.data;

  if (request.status) {
    filteredTickets = filteredTickets.filter(
      (ticket) => ticket.status === request.status
    );
  }

  if (request.category && request.category.length > 0) {
    filteredTickets = filteredTickets.filter((ticket) =>
      request.category?.includes(ticket.category)
    );
  }

  if (request.search && request.search.trim() !== '') {
    const searchTerm = request.search.toLowerCase().trim();
    filteredTickets = filteredTickets.filter((ticket) =>
      ticket.title.toLowerCase().includes(searchTerm)
    );
  }

  return {
    tickets: filteredTickets,
  };
}
